import { useEffect, useState } from "react";
import { ArrowUpRight, Clock, AlertTriangle } from "lucide-react";
import {
  ORDER_URL,
  timeUntilExpiry,
  LOCK_ALL_SCREENS,
  OVERDUE_MESSAGE,
} from "../../config/preview";

// Slim sticky top bar shown above the whole app while PAID = false.
// - Normal preview mode: dark bar with countdown pill.
// - Overdue mode (LOCK_ALL_SCREENS = true): red bar with alert icon
//   and a stronger label. Countdown becomes hour-based when < 24h.
export function PreviewTopBar() {
  // Live tick so the countdown updates each minute without a reload.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const t = timeUntilExpiry(now);

  let countdownLabel;
  if (t.expired) countdownLabel = "Expired";
  else if (t.days > 1) countdownLabel = `${t.days} days left`;
  else if (t.days === 1) countdownLabel = "1 day left";
  else if (t.hours > 1) countdownLabel = `${t.hours} hours left`;
  else countdownLabel = "1 hour left";

  // Colour tone for the countdown pill.
  const isCritical = LOCK_ALL_SCREENS || t.expired || (t.days === 0 && t.hours <= 24);
  const pillTone = isCritical
    ? "border-white/40 bg-white/10 text-white"
    : t.days <= 3
    ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
    : t.days <= 7
    ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
    : "border-slate-500/40 bg-slate-500/10 text-slate-300";

  const barBase =
    "fixed top-0 left-0 right-0 h-10 z-[9999] flex items-center justify-between px-3 sm:px-5 text-[12px] sm:text-[13px] border-b";
  const barTone = LOCK_ALL_SCREENS
    ? "bg-rose-700 text-white border-rose-900"
    : "bg-slate-950 text-slate-100 border-black/50";

  const mainLabel = LOCK_ALL_SCREENS
    ? OVERDUE_MESSAGE
    : "Preview mode — payment pending.";

  const shortMobile = LOCK_ALL_SCREENS ? "Payment overdue." : "Payment pending.";

  return (
    <div
      data-testid="preview-topbar"
      className={`preview-topbar ${barBase} ${barTone}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {LOCK_ALL_SCREENS ? (
          <AlertTriangle size={13} strokeWidth={2.25} className="shrink-0" />
        ) : (
          <span
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest border border-amber-500/40 bg-amber-500/10 text-amber-300"
            aria-hidden
          >
            Preview
          </span>
        )}
        <span className="truncate font-medium">
          <span className="hidden sm:inline">{mainLabel}</span>
          <span className="sm:hidden">{shortMobile}</span>
        </span>
        <span
          data-testid="preview-countdown"
          className={`hidden sm:inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest border ${pillTone}`}
        >
          <Clock size={10} strokeWidth={2} />
          {countdownLabel}
        </span>
      </div>
      <a
        href={ORDER_URL}
        target="_blank"
        rel="noopener"
        data-testid="preview-topbar-cta"
        className={`inline-flex items-center gap-1 rounded-sm transition-colors px-2.5 py-1 text-[12px] font-semibold shrink-0 ${
          LOCK_ALL_SCREENS
            ? "bg-white text-rose-700 hover:bg-rose-50"
            : "bg-white text-slate-950 hover:bg-slate-200"
        }`}
      >
        <span className="hidden sm:inline">Complete payment</span>
        <span className="sm:hidden" data-testid="preview-countdown-mobile">
          {t.expired
            ? "Pay"
            : t.days > 0
            ? `Pay · ${t.days}d`
            : `Pay · ${t.hours}h`}
        </span>
        <ArrowUpRight size={13} strokeWidth={2.25} />
      </a>
    </div>
  );
}
